import type { Vec2 } from "kaplay";
import type { SlotRect } from "../render/background";

// Zombies.
// - EMERGING: clawing in a window slot with a crawl timer; any hit kills it.
// - WALKER: falls out, shambles toward the player scaling up + drifting to
//   center; only a HEADSHOT kills, body shots stagger; reaching the player =
//   game over. Head/body hitboxes are recomputed each frame from pos + scale.

const SKIN_L = [127, 168, 74] as const;
const SKIN_M = [92, 125, 52] as const;
const SKIN_D = [54, 72, 31] as const;
const EYE = [232, 224, 42] as const;

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

// All emerging zombies render at this fixed size (independent of window size),
// so they're uniform. Walker base scale is bumped to match it at spawn.
const EMERGE_W = 39; // 46 - 15%
const EMERGE_H = 44; // 52 - 15%
const WALKER_BASE = 0.9295; // initial walker scale (0.845 + 10%); also the door/falling zombie size
const WALKER_GROW = 2.2; // added over the approach (0.845-era 2.0 + 10%)

// --- shared effects ------------------------------------------------------

export function spawnDeathSplat(x: number, y: number, s: number) {
  const p = add([pos(x, y), z(80)]);
  const max = 0.32;
  let life = max;
  p.onUpdate(() => {
    life -= dt();
    if (life <= 0) destroy(p);
  });
  p.onDraw(() => {
    const k = 1 - life / max;
    const op = life / max;
    drawCircle({ radius: (6 + 18 * k) * s, color: rgb(184, 31, 31), opacity: op * 0.8 });
    drawCircle({ radius: (4 + 12 * k) * s, color: rgb(122, 18, 18), opacity: op });
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2;
      const d = (8 + 20 * k) * s;
      drawRect({ pos: vec2(Math.cos(a) * d, Math.sin(a) * d), width: 4 * s, height: 4 * s, color: rgb(92, 125, 52), opacity: op, anchor: "center" });
    }
  });
}

function spawnSpark(x: number, y: number) {
  const s = add([pos(x, y), z(85)]);
  const max = 0.12;
  let life = max;
  s.onUpdate(() => {
    life -= dt();
    if (life <= 0) destroy(s);
  });
  s.onDraw(() => {
    const op = life / max;
    drawCircle({ radius: 9, color: rgb(255, 138, 30), opacity: op * 0.5 });
    drawCircle({ radius: 5, color: rgb(255, 246, 207), opacity: op });
  });
}

// --- EMERGING ------------------------------------------------------------

function drawEmerging(w: number, h: number, t: number, phase: number) {
  const claw = Math.sin(t * 6 + phase) * 5;
  const bob = Math.sin(t * 3 + phase) * 2;
  const hh = h * 0.5;
  drawRect({ pos: vec2(0, hh * 0.5), width: w * 0.74, height: hh * 1.1, color: rgb(...SKIN_M), anchor: "center" });
  drawRect({ pos: vec2(-w * 0.18, hh * 0.42), width: w * 0.2, height: hh * 0.9, color: rgb(...SKIN_D), anchor: "center" });
  drawRect({ pos: vec2(-w * 0.34, -hh * 0.05 + claw), width: 5, height: hh * 0.95, color: rgb(...SKIN_D), anchor: "center" });
  drawRect({ pos: vec2(w * 0.34, -hh * 0.05 - claw), width: 5, height: hh * 0.95, color: rgb(...SKIN_D), anchor: "center" });
  drawRect({ pos: vec2(-w * 0.36, -hh * 0.5 + claw), width: 8, height: 6, color: rgb(...SKIN_M), anchor: "center" });
  drawRect({ pos: vec2(w * 0.36, -hh * 0.5 - claw), width: 8, height: 6, color: rgb(...SKIN_M), anchor: "center" });
  drawRect({ pos: vec2(0, -hh * 0.12 + bob), width: w * 0.46, height: h * 0.34, color: rgb(...SKIN_L), anchor: "center" });
  drawRect({ pos: vec2(0, -hh * 0.24 + bob), width: w * 0.46, height: 4, color: rgb(...SKIN_D), anchor: "center" });
  drawRect({ pos: vec2(-w * 0.11, -hh * 0.12 + bob), width: 5, height: 5, color: rgb(...EYE), anchor: "center" });
  drawRect({ pos: vec2(w * 0.11, -hh * 0.12 + bob), width: 5, height: 5, color: rgb(...EYE), anchor: "center" });
  drawRect({ pos: vec2(0, hh * 0.04 + bob), width: w * 0.3, height: 3, color: rgb(...SKIN_D), anchor: "center" });
}

export interface Zombie {
  slotIndex: number;
  isAlive(): boolean;
  contains(p: Vec2, margin: number): boolean;
  killInSlot(): void;
  destroyNow(): void;
}

export function addEmergingZombie(
  slot: SlotRect,
  slotIndex: number,
  crawlTime: number,
  isPaused: () => boolean,
  onCrawlExpire: (z: Zombie) => void,
): Zombie {
  const cx = slot.x + slot.w / 2;
  const isDoor = slot.kind === "door";
  // Vertical placement via `emergeOffset` (px above the slot bottom): for a
  // window it positions the sprite centre (default = slot centre); a door keeps
  // its centre for hit-testing and uses the offset for the feet height below.
  const cy = isDoor ? slot.y + slot.h / 2 : slot.y + slot.h - (slot.emergeOffset ?? slot.h / 2);
  // door uses the full slot for hit-testing; windows use the uniform sprite size
  const w = isDoor ? slot.w : EMERGE_W;
  const h = isDoor ? slot.h : EMERGE_H;
  const phase = rand(0, Math.PI * 2);
  let crawl = crawlTime;
  let alive = true;
  let expired = false;

  const obj = add([pos(cx, cy), rotate(slot.rot ?? 0), z(50), "zombie", "emerging"]);

  obj.onUpdate(() => {
    if (!alive || expired || isPaused()) return;
    crawl -= dt();
    if (crawl <= 0) {
      expired = true;
      onCrawlExpire(zombie);
    }
  });
  obj.onDraw(() => {
    if (isDoor) {
      // a full-body walker standing in the doorway (same size as a fresh walker)
      const s = WALKER_BASE;
      pushTransform();
      pushTranslate(0, slot.h / 2 - (slot.emergeOffset ?? 6)); // feet `emergeOffset`px above the door bottom
      pushScale(s, s);
      drawWalker(time(), phase, false);
      popTransform();
    } else {
      drawEmerging(w, h, time(), phase);
    }
  });

  const zombie: Zombie = {
    slotIndex,
    isAlive: () => alive,
    contains(p, margin) {
      return Math.abs(p.x - cx) <= w / 2 + margin && Math.abs(p.y - cy) <= h / 2 + margin;
    },
    killInSlot() {
      if (!alive) return;
      alive = false;
      spawnDeathSplat(cx, cy, 1);
      destroy(obj);
    },
    destroyNow() {
      alive = false;
      destroy(obj);
    },
  };
  return zombie;
}

// --- WALKER --------------------------------------------------------------

// Local layout (unscaled, origin at the feet): head center at y=-52 r=9,
// body box centered at y=-30 (half 16 x 26).
// kill (0..1): the lunge pose when this walker reaches the player — arms raise
// overhead and the mouth gapes open.
function drawWalker(t: number, phase: number, flash: boolean, kill = 0) {
  const step = Math.sin(t * 6 + phase);
  const sway = Math.sin(t * 3 + phase) * 2;
  const arm = Math.sin(t * 4 + phase) * 4 * (1 - kill); // no idle swing during the lunge
  const torso: readonly [number, number, number] = flash ? [210, 240, 190] : SKIN_M;

  drawRect({ pos: vec2(-6, step > 0 ? -2 : 0), width: 7, height: 18, color: rgb(...SKIN_D), anchor: "bot" });
  drawRect({ pos: vec2(6, step < 0 ? -2 : 0), width: 7, height: 18, color: rgb(...SKIN_D), anchor: "bot" });
  drawRect({ pos: vec2(sway, -32), width: 22, height: 30, color: rgb(...torso), anchor: "center" });
  drawRect({ pos: vec2(sway - 5, -32), width: 7, height: 30, color: rgb(...SKIN_D), anchor: "center" });
  // arms (raise overhead when killing)
  const armY = -34 - 16 * kill;
  const handY = -22 - 40 * kill;
  const spread = 13 + 4 * kill;
  drawRect({ pos: vec2(sway - spread, armY + arm), width: 5, height: 20, color: rgb(...SKIN_D), anchor: "center" });
  drawRect({ pos: vec2(sway + spread, armY - arm), width: 5, height: 20, color: rgb(...SKIN_D), anchor: "center" });
  drawRect({ pos: vec2(sway - spread, handY + arm), width: 7, height: 6, color: rgb(...SKIN_M), anchor: "center" });
  drawRect({ pos: vec2(sway + spread, handY - arm), width: 7, height: 6, color: rgb(...SKIN_M), anchor: "center" });
  drawRect({ pos: vec2(sway, -52), width: 18, height: 18, color: rgb(...SKIN_L), anchor: "center" });
  drawRect({ pos: vec2(sway, -59), width: 18, height: 4, color: rgb(...SKIN_D), anchor: "center" });
  drawRect({ pos: vec2(sway - 4, -52), width: 4, height: 4, color: rgb(...EYE), anchor: "center" });
  drawRect({ pos: vec2(sway + 4, -52), width: 4, height: 4, color: rgb(...EYE), anchor: "center" });
  // mouth (gapes open when killing)
  drawRect({ pos: vec2(sway, -46 + kill), width: 10 + 2 * kill, height: 2 + 7 * kill, color: kill > 0.3 ? rgb(70, 16, 16) : rgb(...SKIN_D), anchor: "center" });
}

// Zombie seen from BEHIND (no face), centered on the origin so it can be
// flipped/rotated cleanly. Used for the head-down dive.
function drawWalkerBackCentered(t: number, phase: number) {
  const step = Math.sin(t * 6 + phase);
  const sway = Math.sin(t * 3 + phase) * 2;
  const arm = Math.sin(t * 4 + phase) * 4;
  drawRect({ pos: vec2(-6, step > 0 ? 28 : 30), width: 7, height: 18, color: rgb(...SKIN_D), anchor: "bot" });
  drawRect({ pos: vec2(6, step < 0 ? 28 : 30), width: 7, height: 18, color: rgb(...SKIN_D), anchor: "bot" });
  drawRect({ pos: vec2(sway, -2), width: 22, height: 30, color: rgb(...SKIN_M), anchor: "center" });
  drawRect({ pos: vec2(sway, -2), width: 3, height: 30, color: rgb(...SKIN_D), anchor: "center" }); // spine
  drawRect({ pos: vec2(sway - 13, -4 + arm), width: 5, height: 20, color: rgb(...SKIN_D), anchor: "center" });
  drawRect({ pos: vec2(sway + 13, -4 - arm), width: 5, height: 20, color: rgb(...SKIN_D), anchor: "center" });
  drawRect({ pos: vec2(sway - 13, 8 + arm), width: 7, height: 6, color: rgb(...SKIN_M), anchor: "center" });
  drawRect({ pos: vec2(sway + 13, 8 - arm), width: 7, height: 6, color: rgb(...SKIN_M), anchor: "center" });
  drawRect({ pos: vec2(sway, -22), width: 18, height: 18, color: rgb(...SKIN_L), anchor: "center" }); // back of head
  drawRect({ pos: vec2(sway, -26), width: 18, height: 5, color: rgb(...SKIN_M), anchor: "center" }); // hair/shade
}

// Crumpled heap of green pixels on impact (feet-origin, base at y=0).
function drawPile() {
  const blocks: Array<readonly [number, number, number, number, readonly [number, number, number]]> = [
    [-17, -1, 8, 5, SKIN_D],
    [-13, -3, 11, 8, SKIN_M],
    [-3, -5, 13, 11, SKIN_L],
    [9, -3, 11, 8, SKIN_M],
    [13, -1, 7, 5, SKIN_D],
    [-9, -9, 8, 6, SKIN_M],
    [2, -10, 9, 7, SKIN_L],
  ];
  for (const [x, y, w, h, c] of blocks) {
    drawRect({ pos: vec2(x, y), width: w, height: h, color: rgb(...c), anchor: "bot" });
  }
}

// --- FALLING (window -> walker transition) -------------------------------

export interface Faller {
  isAlive(): boolean;
  contains(p: Vec2, margin: number): boolean;
  kill(): void; // shot mid-fall: splat + cancel the walker spawn
  destroyNow(): void; // silent removal (level swap)
}

// Plays once: dive head-down out of the window, hit the ground, collapse into a
// heap, then rise to standing. Calls onDone(x) when finished (spawns a walker).
// Shootable in flight (anywhere in the body) via the returned Faller; a kill
// cancels onDone so no walker spawns.
export function addFallingZombie(opts: {
  cx: number;
  cy: number;
  groundY: number; // y where the feet land (= walker start y)
  isPaused: () => boolean;
  onDone: (x: number) => void;
  freezeAt?: number; // debug: freeze at 0..1 of the total timeline
}): Faller {
  const phase = rand(0, Math.PI * 2);
  const s = WALKER_BASE;
  const PILE = 0.25;
  const RISE = 0.4;

  // During the dive the body is centered on the pivot; its flipped head reaches
  // the ground when center.y ~ groundY - 24*s.
  const diveEndY = opts.groundY - 24 * s;

  // Constant-gravity fall: duration scales with sqrt(distance) (the dive ease is
  // k*k), so a short drop is quick/gentle and a tall drop takes longer + faster.
  const fallDist = Math.max(0, diveEndY - opts.cy);
  const FALL = Math.min(0.7, Math.max(0.12, 0.04 * Math.sqrt(fallDist)));
  const total = FALL + PILE + RISE;

  let t = opts.freezeAt != null ? opts.freezeAt * total : 0;
  let mode: "fall" | "pile" | "rise" = "fall";
  let alive = true;

  const obj = add([pos(opts.cx, opts.cy), scale(s), z(70), "falling", "zombie"]);

  const bodyH = 61; // local feet->head height; world height = bodyH * s

  function frame() {
    if (t < FALL) {
      mode = "fall";
      const k = t / FALL;
      obj.pos.y = lerp(opts.cy, diveEndY, k * k); // gravity accel
    } else if (t < FALL + PILE) {
      mode = "pile";
      obj.pos.y = opts.groundY;
    } else {
      mode = "rise";
      const k = Math.min(1, (t - FALL - PILE) / RISE);
      // slide up out of the ground: feet start 3/4 below the line, end on it
      obj.pos.y = lerp(opts.groundY + 0.75 * bodyH * s, opts.groundY, k);
    }
    obj.pos.x = opts.cx;
  }
  frame();

  obj.onUpdate(() => {
    if (!alive || opts.freezeAt != null || opts.isPaused()) return;
    t += dt();
    if (t >= total) {
      alive = false;
      opts.onDone(opts.cx);
      destroy(obj);
      return;
    }
    frame();
  });
  obj.onDraw(() => {
    if (mode === "fall") {
      // back view, flipped over the x-axis (head down)
      pushTransform();
      pushScale(1, -1);
      drawWalkerBackCentered(time(), phase);
      popTransform();
    } else if (mode === "pile") {
      drawPile();
    } else {
      // rise: clip everything below the ground line so it emerges from the dirt
      const localGround = (opts.groundY - obj.pos.y) / s;
      drawMasked(
        () => drawWalker(time(), phase, false),
        () => drawRect({ pos: vec2(-60, -2000), width: 120, height: 2000 + localGround, color: rgb(255, 255, 255) }),
      );
    }
  });

  // Generous full-body hitbox tracking the sprite for the current phase, so a
  // shot lands anywhere on the falling/piled/rising zombie.
  function hitbox() {
    if (mode === "pile") return { cy: obj.pos.y - 6 * s, halfH: 12 * s };
    if (mode === "rise") {
      const top = obj.pos.y - 52 * s; // head; body is clipped at groundY below
      return { cy: (top + opts.groundY) / 2, halfH: (opts.groundY - top) / 2 };
    }
    return { cy: obj.pos.y, halfH: 32 * s }; // dive: head-down, spans the pivot
  }

  return {
    isAlive: () => alive,
    contains(pt, margin) {
      if (!alive) return false;
      const { cy, halfH } = hitbox();
      return Math.abs(pt.x - obj.pos.x) <= 18 * s + margin && Math.abs(pt.y - cy) <= halfH + margin;
    },
    kill() {
      if (!alive) return;
      alive = false;
      const { cy } = hitbox();
      spawnDeathSplat(obj.pos.x, cy, s);
      destroy(obj); // onDone never fires → no walker spawns
    },
    destroyNow() {
      alive = false;
      destroy(obj); // silent removal (level swap); no walker spawns
    },
  };
}

export interface Walker {
  isAlive(): boolean;
  nearness(): number; // 0 far -> 1 at player (for hit ordering)
  headContains(p: Vec2, margin: number): boolean;
  bodyContains(p: Vec2, margin: number): boolean;
  headshot(): void; // kill + blood
  stagger(p: Vec2): void; // body shot: knockback + spark, no kill
  destroyNow(): void; // silent removal (level swap)
}

export function addWalkerZombie(opts: {
  startX: number;
  startY: number;
  traverse: number;
  isPaused: () => boolean;
  onReachPlayer: () => void;
  onLunge?: () => void; // the walker reached the player and started its lunge (for the growl SFX)
  startProgress?: number; // debug: spawn already-advanced
}): Walker {
  const phase = rand(0, Math.PI * 2);
  const endY = height() - 6;
  // Equal screen speed for every walker: scale traverse time by path length so
  // side walkers (longer diagonal toward center) don't outrun central ones.
  const dx = (width() / 2 - opts.startX) * 0.6;
  const dy = endY - opts.startY;
  const traverse = opts.traverse * (Math.hypot(dx, dy) / Math.abs(dy));
  const KILL_DUR = 0.5; // lunge time before game over
  const KILL_RAISE = 0.32; // time to fully raise arms / open mouth
  let p = opts.startProgress ?? 0;
  let alive = true;
  let staggerT = 0;
  let killing = false; // reached the player: playing the lunge
  let killT = 0;
  let reached = false;

  const obj = add([pos(opts.startX, opts.startY), scale(0.6), z(60), "zombie", "walker"]);

  // Visual progress eases in (p^1.8): slow while far, accelerating as it nears.
  // p stays linear in time, so the walker still reaches the player at `traverse`.
  const vis = () => Math.pow(p, 1.8);
  const curScale = () => WALKER_BASE + WALKER_GROW * vis();
  const headCenter = () => obj.pos.add(0, -52 * curScale());

  function applyTransform() {
    const v = vis();
    obj.scaleTo(WALKER_BASE + WALKER_GROW * v);
    const shake = staggerT > 0 ? Math.sin(time() * 60) * 4 : 0;
    obj.pos.x = lerp(opts.startX, width() / 2, v * 0.6) + shake;
    obj.pos.y = lerp(opts.startY, endY, v);
    // nearer (more advanced) walkers draw over farther ones; stay under HUD (120)
    obj.z = 60 + v * 40;
  }
  applyTransform();

  obj.onUpdate(() => {
    if (!alive || opts.isPaused()) return;
    if (killing) {
      // lunge in place, then end the game (kept alive but unshootable)
      killT += dt();
      if (!reached && killT >= KILL_DUR) {
        reached = true;
        opts.onReachPlayer();
      }
      return;
    }
    p = Math.min(1, p + dt() / traverse);
    if (staggerT > 0) staggerT -= dt();
    applyTransform();
    if (p >= 1) {
      killing = true;
      killT = 0;
      opts.onLunge?.(); // growl as the arms raise and the mouth gapes
    }
  });
  obj.onDraw(() => drawWalker(time(), phase, staggerT > 0, killing ? Math.min(1, killT / KILL_RAISE) : 0));

  return {
    isAlive: () => alive,
    nearness: () => p,
    headContains(pt, margin) {
      return !killing && headCenter().dist(pt) <= 9 * curScale() + margin;
    },
    bodyContains(pt, margin) {
      if (killing) return false;
      const c = obj.pos.add(0, -30 * curScale());
      const s = curScale();
      return Math.abs(pt.x - c.x) <= 16 * s + margin && Math.abs(pt.y - c.y) <= 26 * s + margin;
    },
    headshot() {
      if (!alive) return;
      alive = false;
      const hc = headCenter();
      spawnDeathSplat(hc.x, hc.y, curScale());
      destroy(obj);
    },
    stagger(pt) {
      if (!alive) return;
      staggerT = 0.25;
      p = Math.max(0, p - 0.06);
      spawnSpark(pt.x, pt.y);
    },
    destroyNow() {
      alive = false;
      destroy(obj);
    },
  };
}
